/**
 * React wrapper for Bryntum ResourceHistogram
 */
import React, { RefObject } from 'react';
import { AjaxStore, AjaxStoreConfig, AlignSpec, Base, CellCopyPaste, CellCopyPasteConfig, CellEdit, CellEditConfig, CellMenu, CellMenuConfig, CellTooltip, CellTooltipConfig, CheckColumnConfig, Column, ColumnAutoWidth, ColumnAutoWidthConfig, ColumnDragToolbar, ColumnDragToolbarConfig, ColumnLines, ColumnLinesConfig, ColumnPicker, ColumnPickerConfig, ColumnRename, ColumnRenameConfig, ColumnReorder, ColumnReorderConfig, ColumnResize, ColumnResizeConfig, ColumnStore, ColumnStoreConfig, Container, ContainerItemConfig, ContainerLayoutConfig, CrudManager, CrudManagerConfig, DomConfig, DragHelper, Editor, EventColor, ExportDialog, Field, FileDrop, FileDropConfig, FillHandle, FillHandleConfig, Filter, FilterBar, FilterBarConfig, FilterConfig, Grid, GridBase, GridColumnConfig, GridExcelExporter, GridExcelExporterConfig, GridPdfExport, GridPdfExportConfig, GridPrint, GridPrintConfig, GridRowResize, GridRowResizeConfig, Group, GroupConfig, GroupSummary, GroupSummaryConfig, HeaderMenu, HeaderMenuConfig, HeaderZoom, HeaderZoomConfig, Histogram, HistogramConfig, HistogramRenderData, HistogramSeries, Layout, Location, Mask, MaskConfig, Menu, MenuItem, MenuItemConfig, MergeCells, MergeCellsConfig, Model, ModelConfig, NonWorkingTime, NonWorkingTimeConfig, Pan, PanConfig, Panel, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, Popup, PreserveScrollOptions, PresetStore, ProjectModel, ProjectModelConfig, QuickFind, QuickFindConfig, RecordPositionContext, Rectangle, RegionResize, RegionResizeConfig, ResourceAllocationInterval, ResourceHeaderConfig, ResourceHistogram, ResourceHistogramFeaturesConfigType, ResourceMarginConfig, ResourceMenu, ResourceMenuConfig, ResourceModel, ResourceNonWorkingTime, ResourceNonWorkingTimeConfig, ResourceTimeRangeModel, ResourceTimeRangeModelConfig, ResourceTimeRangeStore, ResourceTimeRangeStoreConfig, ResourceTimeRanges, ResourceTimeRangesConfig, Row, RowCopyPaste, RowCopyPasteConfig, RowExpander, RowExpanderConfig, RowNumberColumnConfig, RowReorder, RowReorderConfig, ScalePoint, ScheduleTooltip, ScheduleTooltipConfig, Scheduler, SchedulerEventModel, SchedulerPro, SchedulerResourceModel, ScrollManager, ScrollManagerConfig, Scroller, ScrollerConfig, Search, SearchConfig, Sort, SortConfig, Split, SplitConfig, StateProvider, StickyCells, StickyCellsConfig, Store, StoreConfig, StoreSearchResult, Stripe, StripeConfig, SubGrid, SubGridConfig, Summary, SummaryConfig, TabConfig, TimeAxis, TimeAxisColumn, TimeAxisConfig, TimeAxisHeaderMenu, TimeAxisHeaderMenuConfig, TimeRanges, TimeRangesConfig, TimeSpan, TimeSpanConfig, TimeSpanHighlight, TimeSpanHighlightConfig, TimelineBase, TimelineContext, TimelineHistogram, Tool, ToolConfig, ToolbarConfig, Tooltip, TooltipConfig, Tree, TreeConfig, TreeGroup, TreeGroupConfig, VerticalTimeAxisColumn, ViewPreset, ViewPresetConfig, VisibleDate, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumResourceHistogramProps =  {
     // Configs only
    adopt?                            : HTMLElement|string
    aggregateDataEntry?               : ((aggregateDataEntry: { aggregated: object, entry: object, arrayIndex: number, entryIndex: object[] }) => object)|string
    aggregateHistogramDataForGroups?  : boolean
    align?                            : AlignSpec|string
    allowOver?                        : boolean
    anchor?                           : boolean
    anchorToTarget?                   : boolean
    animateRemovingRows?              : boolean
    ariaDescription?                  : string
    ariaLabel?                        : string
    autoAdjustTimeAxis?               : boolean
    autoClose?                        : boolean
    autoHeight?                       : boolean
    autoHide?                         : boolean
    autoShow?                         : boolean
    autoUpdateRecord?                 : boolean
    barTextEffortUnit?                : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    barTipEffortUnit?                 : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    barTooltipTemplate?               : (context: { datum: ResourceAllocationInterval, tip: Tooltip, element: HTMLElement, activeTarget: HTMLElement, event: Event }, data: { record: ResourceModel }) => string
    bbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                          : string|object
    bubbleEvents?                     : object
    bufferCoef?                       : number
    bufferThreshold?                  : number
    callOnFunctions?                  : boolean
    centered?                         : boolean
    closable?                         : boolean
    closeAction?                      : 'hide'|'destroy'
    collapsed?                        : boolean
    collapsible?                      : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    columns?                          : ColumnStore|Partial<GridColumnConfig>[]|Partial<ColumnStoreConfig>
    config?                           : object
    constrainTo?                      : HTMLElement|Widget|Rectangle
    contentElementCls?                : string|object
    contextMenuTriggerEvent?          : 'contextmenu'|'click'|'dblclick'
    crudManagerClass?                 : typeof CrudManager
    dataModelField?                   : string
    defaultBindProperty?              : string
    defaultFocus?                     : (widget: Widget) => boolean
    defaultRegion?                    : string
    defaultResourceImageName?         : string
    defaults?                         : object
    destroyStore?                     : boolean
    destroyStores?                    : boolean
    disableGridRowModelWarning?       : boolean
    dismissDelay?                     : number
    dock?                             : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                        : boolean|{
        handleSelector?: string
    }
    durationDisplayPrecision?         : number|boolean
    effortFormat?                     : string
    effortUnit?                       : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    enableRecurringEvents?            : boolean
    enableSticky?                     : boolean
    enableTextSelection?              : boolean
    endParamName?                     : string
    features?                         : Partial<ResourceHistogramFeaturesConfigType>
    fillLastColumn?                   : boolean
    fixedRowHeight?                   : boolean
    floating?                         : boolean
    focusOnToFront?                   : boolean
    footer?                           : {
        dock?: 'top'|'right'|'bottom'|'left'|'start'|'end'
        html?: string
        cls?: string
    }|string
    forElement?                       : HTMLElement
    forSelector?                      : string
    fullRowRefresh?                   : boolean
    getBarText?                       : (datum: ResourceAllocationInterval, index: number) => string
    getBarTip?                        : (series: HistogramSeries, rectConfig: DomConfig, datum: object, index: number) => void
    getDataEntryForAggregating?       : ((getDataEntryForAggregating: { entry: object }) => object)|string
    getHtml?                          : ((context: { tip: Tooltip, element: HTMLElement, activeTarget: HTMLElement, event: Event }) => string|Promise<any>)|string
    getOutlineClass?                  : (series: HistogramSeries, data: object[], renderData: HistogramRenderData) => string
    getRecordData?                    : ((getRecordData: { record: Model }, aggregationContext?: object) => object)|string
    getRectClass?                     : (series: HistogramSeries, rectConfig: DomConfig, datum: object, index: number, renderData: HistogramRenderData) => string
    getRowHeight?                     : (getRowHeight: { record: Model }) => number
    hardRefreshOnTimeAxisReconfigure? : boolean
    header?                           : string|boolean|PanelHeader
    hideAnimation?                    : boolean|object
    hideDelay?                        : number|boolean
    hideHorizontalScrollbar?          : boolean
    hideOnDelegateChange?             : boolean
    hideWhenEmpty?                    : boolean
    histogramWidgetClass?             : Histogram
    hoverDelay?                       : number
    htmlCls?                          : string|object
    ignoreDomEventsWhileScrolling?    : boolean
    ignoreParentReadOnly?             : boolean
    includeInactiveEvents?            : boolean
    infiniteScroll?                   : boolean
    initAggregatedDataEntry?          : (() => object)|string
    itemCls?                          : string
    keyMap?                           : Record<string, string>
    lazyItems?                        : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?                        : object
    loadingMsg?                       : string
    loadMask?                         : string|Partial<MaskConfig>|null
    loadMaskDefaults?                 : Partial<MaskConfig>
    loadMaskError?                    : Partial<MaskConfig>|Mask|boolean
    localeClass?                      : typeof Base
    localizable?                      : boolean
    localizableProperties?            : string[]
    managedEventSizing?               : boolean
    maskDefaults?                     : Partial<MaskConfig>
    masked?                           : boolean|string|Partial<MaskConfig>
    maximizable?                      : boolean
    maximized?                        : boolean
    maxTimeAxisUnit?                  : 'millisecond'|'second'|'minute'|'hour'|'day'|'week'|'month'|'quarter'|'year'
    modal?                            : boolean|{
        closeOnMaskTap?: boolean
        transparent?: boolean
    }
    monitorResize?                    : boolean
    mouseOffsetX?                     : number
    mouseOffsetY?                     : number
    namedItems?                       : Record<string, Partial<ContainerItemConfig>>
    owner?                            : Widget
    partner?                          : TimelineBase
    passStartEndParameters?           : boolean
    plugins?                          : Function[]
    positioned?                       : boolean
    preserveFocusOnDatasetChange?     : boolean
    preserveScrollOnDatasetChange?    : boolean
    preventTooltipOnTouch?            : boolean
    relayStoreEvents?                 : boolean
    resizeToFitIncludesHeader?        : boolean
    resourceColumns?                  : Partial<ResourceHeaderConfig>
    resourceImageExtension?           : string
    resourceImagePath?                : string
    responsiveLevels?                 : Record<string, number|string>
    ripple?                           : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?                      : ShadowRoot
    scaleColumn?                      : object
    scrollAction?                     : 'hide'|'realign'|null
    scrollerClass?                    : typeof Scroller
    scrollManager?                    : Partial<ScrollManagerConfig>|ScrollManager
    selectionMode?                    : object
    series?                           : Record<string, HistogramSeries>
    showAnimation?                    : boolean|object
    showBarText?                      : boolean
    showDirty?                        : boolean|{
        duringEdit?: boolean
    }
    showEffortUnit?                   : boolean
    showMaxEffort?                    : boolean
    showOnClick?                      : boolean
    showOnHover?                      : boolean
    showTooltipWhenDisabled?          : boolean
    snapRelativeToEventStartDate?     : boolean
    startParamName?                   : string
    stateful?                         : boolean|object|string[]
    statefulEvents?                   : object|string[]
    stateId?                          : string
    stateProvider?                    : StateProvider
    stickyHeaders?                    : boolean
    strips?                           : Record<string, Partial<ContainerItemConfig>>
    subGridConfigs?                   : Record<string, Partial<SubGridConfig>>
    suppressFit?                      : boolean
    syncMask?                         : string|Partial<MaskConfig>|null
    tab?                              : boolean|Partial<TabConfig>
    tag?                              : string
    tbar?                             : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    textAlign?                        : 'left'|'center'|'right'|'start'|'end'
    textContent?                      : boolean
    timeAxis?                         : Partial<TimeAxisConfig>|TimeAxis
    trackMouse?                       : boolean
    trapFocus?                        : boolean
    ui?                               : string|object
    visibleZoomFactor?                : number
    weekStartDay?                     : number
    weight?                           : number
    zoomKeepsOriginalTimespan?        : boolean
    zoomOnMouseWheel?                 : boolean
    zoomOnTimeAxisDoubleClick?        : boolean

     // Configs and properties
    alignSelf?              : string
    animateTreeNodeToggle?  : boolean
    appendTo?               : HTMLElement|string
    barMargin?              : number
    cls?                    : string|object
    columnLines?            : boolean
    content?                : string
    crudManager?            : CrudManager|Partial<CrudManagerConfig>
    data?                   : object[]|Model[]|Partial<ModelConfig>[]
    dataset?                : object
    disabled?               : boolean
    displayDateFormat?      : string
    emptyText?              : string
    enableEventAnimations?  : boolean
    enableUndoRedoKeys?     : boolean
    endDate?                : Date|string
    eventColor?             : EventColor
    eventStyle?             : 'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'|null
    extraData?              : string|number|boolean|object
    fillTicks?              : boolean
    flex?                   : number|string
    forceFit?               : boolean
    height?                 : number|string
    hidden?                 : boolean
    hideFooters?            : boolean
    hideHeaders?            : boolean
    hideRowHover?           : boolean
    histogramWidget?        : Histogram|Partial<HistogramConfig>
    html?                   : string|((widget: Widget) => string)
    id?                     : string
    insertBefore?           : HTMLElement|string
    insertFirst?            : HTMLElement|string
    items?                  : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?                 : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?            : object
    longPressTime?          : number
    margin?                 : number|string
    maxHeight?              : string|number
    maximizeOnMobile?       : number|string
    maxWidth?               : string|number
    maxZoomLevel?           : number
    minHeight?              : string|number
    minWidth?               : string|number
    minZoomLevel?           : number
    preserveScroll?         : PreserveScrollOptions|boolean
    presets?                : PresetStore|Partial<ViewPresetConfig>[]
    project?                : Partial<ProjectModel|ProjectModelConfig>|RefObject<any>
    readOnly?               : boolean
    record?                 : Model
    resourceMargin?         : number|Partial<ResourceMarginConfig>
    resourceTimeRanges?     : ResourceTimeRangeModel[]|Partial<ResourceTimeRangeModelConfig>[]
    resourceTimeRangeStore? : ResourceTimeRangeStore|Partial<ResourceTimeRangeStoreConfig>
    rowHeight?              : number
    rowLines?               : boolean
    scrollable?             : Scroller|boolean|Partial<ScrollerConfig>
    showBarTip?             : boolean|Partial<TooltipConfig>
    snap?                   : boolean
    startDate?              : Date|string
    store?                  : Partial<Store|StoreConfig|AjaxStore|AjaxStoreConfig>
    strictRecordMapping?    : boolean
    tickSize?               : number
    timeRanges?             : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeRangeStore?         : Store|Partial<StoreConfig>
    timeZone?               : string|number
    title?                  : string
    tools?                  : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?                : string|Partial<TooltipConfig>
    transitionDuration?     : number
    viewPreset?             : ViewPreset|Partial<ViewPresetConfig>|string
    visibleDate?            : object|Date|VisibleDate
    width?                  : number|string
    workingTime?            : object
    x?                      : number
    y?                      : number

     // Properties only
    anchorSize?      : number[]
    eventColors?     : string[]
    eventStyles?     : string[]
    isEngineReady?   : boolean
    isSettingValues? : boolean
    isValid?         : boolean
    scrollLeft?      : number
    scrollTop?       : number
    scrollX?         : number
    selectedCell?    : Location
    selectedCells?   : Location[]
    selectedRecord?  : Model
    selectedRecords? : Model[]|number[]
    selectedRows?    : Model[]|number[]
    state?           : object
    timeResolution?  : object|number
    type?            : string
    values?          : Record<string, object>
    zoomLevel?       : number

     // Events
    onBeforeAssignmentDelete?            : ((event: { source: Scheduler, eventRecords: SchedulerEventModel[], context: { finalize: { removeRecords?: boolean } } }) => boolean|void)|string
    onBeforeCancelCellEdit?              : ((event: { source: Grid, editorContext: Location }) => boolean|void)|string
    onBeforeCellDelete?                  : ((event: { source: Grid, gridSelection: (Location|Model)[] }) => boolean|void)|string
    onBeforeCellEditStart?               : ((event: { source: Grid, editorContext: { column: Column, record: Model, cell: HTMLElement, editor: Field, finalize: { context?: object }, value: any } }) => boolean|void)|string
    onBeforeClose?                       : ((event: { source: Popup }) => void)|string
    onBeforeColumnDragStart?             : ((event: { source: Grid, column: Column, event: Event }) => boolean|void)|string
    onBeforeColumnDropFinalize?          : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, event: Event, region: string }) => boolean|void)|string
    onBeforeCopy?                        : ((event: { source: Grid, cells: Location[], data: string, isCut: boolean, entityName: string }) => Promise<boolean|void>)|string
    onBeforeDestroy?                     : ((event: { source: Base }) => void)|string
    onBeforeEventDelete?                 : ((event: { source: Scheduler|any, eventRecords: SchedulerEventModel[], context: { finalize: { removeRecords?: boolean } } }) => Promise<boolean|void>)|string
    onBeforeFinishCellEdit?              : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onBeforeHide?                        : ((event: { source: Widget }) => boolean|void)|string
    onBeforeHistogramDataCacheSet?       : ((event: { source: TimelineHistogram, record: Model, data: object }) => void)|string
    onBeforePan?                         : ((event: { event: Event }) => boolean|void)|string
    onBeforePaste?                       : ((event: { source: Grid, clipboardData: string, targetCell: Location, entityName: string }) => Promise<boolean|void>)|string
    onBeforePdfExport?                   : ((event: { config: object }) => boolean|void)|string
    onBeforePresetChange?                : ((event: { source: Scheduler, startDate: Date, endDate: Date, from: ViewPreset, to: ViewPreset }) => boolean|void)|string
    onBeforeRenderHistogramRow?          : ((event: { source: TimelineHistogram, record: Model, histogramConfig: Partial<HistogramConfig>, histogramWidget: Histogram }) => void)|string
    onBeforeRenderRecordHistogram?       : ((event: { source: TimelineHistogram, record: Model, histogramConfig: Partial<HistogramConfig>, histogramWidget: Histogram }) => void)|string
    onBeforeRenderRow?                   : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onBeforeRenderRows?                  : ((event: { source: Grid }) => void)|string
    onBeforeSelectionChange?             : ((event: { action: string, mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => boolean|void)|string
    onBeforeSetRecord?                   : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?                        : ((event: { source: { triggeredByEvent: Event } }) => Promise<boolean|void>)|string
    onBeforeToggleGroup?                 : ((event: { groupRecord: Model, collapse: boolean, domEvent: Event }) => boolean|void)|string
    onBeforeToggleNode?                  : ((event: { source: Grid, record: Model, collapse: boolean }) => void)|string
    onCancelCellEdit?                    : ((event: { source: Grid, editorContext: Location, event: Event }) => void)|string
    onCatchAll?                          : ((event: {[key: string]: any, type: string}) => void)|string
    onCellClick?                         : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellContextMenu?                   : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellDblClick?                      : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMenuBeforeShow?                : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => boolean|void)|string
    onCellMenuItem?                      : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model }) => void)|string
    onCellMenuShow?                      : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, record: Model }) => void)|string
    onCellMenuToggleItem?                : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, record: Model, checked: boolean }) => void)|string
    onCellMouseOut?                      : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCellMouseOver?                     : ((event: { grid: Grid, record: Model, column: Column, cellElement: HTMLElement, target: HTMLElement, event: MouseEvent }) => void)|string
    onCollapse?                          : ((event: { source: Panel }) => void)|string
    onCollapseNode?                      : ((event: { source: Grid, record: Model }) => void)|string
    onColumnDrag?                        : ((event: { source: Grid, column: Column, insertBefore: Column, event: Event, context: { valid: boolean } }) => void)|string
    onColumnDragStart?                   : ((event: { source: Grid, column: Column, event: Event }) => void)|string
    onColumnDrop?                        : ((event: { source: Grid, column: Column, insertBefore: Column, newParent: Column, valid: boolean, event: Event, region: string }) => boolean|void)|string
    onContextMenuItem?                   : ((event: { source: Widget, menu: Menu, item: MenuItem }) => void)|string
    onContextMenuToggleItem?             : ((event: { source: Widget, menu: Menu, item: MenuItem, checked: boolean }) => void)|string
    onCopy?                              : ((event: { source: Grid, cells: Location[], copiedDataString: string, isCut: boolean, entityName: string }) => void)|string
    onDataChange?                        : ((event: { source: Grid, store: Store, action: 'remove'|'removeAll'|'add'|'updatemultiple'|'clearchanges'|'filter'|'update'|'dataset'|'replace', record: Model, records: Model[], changes: object }) => void)|string
    onDateRangeChange?                   : ((event: { source: TimelineBase, old: { startDate: Date, endDate: Date }, new: { startDate: Date, endDate: Date } }) => void)|string
    onDestroy?                           : ((event: { source: Base }) => void)|string
    onDragSelecting?                     : ((event: { source: Grid, selectedCells: Model[]|object|Partial<ModelConfig> }) => void)|string
    onExpand?                            : ((event: { source: Panel }) => void)|string
    onExpandNode?                        : ((event: { source: Grid, record: Model }) => void)|string
    onFileDrop?                          : ((event: { source: Grid, file: DataTransferItem, domEvent: DragEvent }) => void)|string
    onFinishCellEdit?                    : ((event: { grid: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onFocusIn?                           : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?                          : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onGenerateScalePoints?               : ((event: { source: ResourceHistogram, scalePoints: ScalePoint[] }) => void)|string
    onGridRowAbort?                      : ((event: { source: DragHelper, context: object, event: MouseEvent }) => void)|string
    onGridRowBeforeDragStart?            : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => boolean|void)|string
    onGridRowBeforeDropFinalize?         : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => Promise<boolean|void>)|string
    onGridRowDrag?                       : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, records: Model[] }, event: MouseEvent }) => void)|string
    onGridRowDragStart?                  : ((event: { source: DragHelper, context: { records: Model[] }, event: MouseEvent|TouchEvent }) => void)|string
    onGridRowDrop?                       : ((event: { source: DragHelper, context: { valid: boolean, insertBefore: Model, parent: Model, record: Model, records: Model[], oldPositionContext: RecordPositionContext[] }, event: MouseEvent }) => void)|string
    onHeaderMenuBeforeShow?              : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => boolean|void)|string
    onHeaderMenuItem?                    : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column }) => void)|string
    onHeaderMenuShow?                    : ((event: { source: Grid, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => void)|string
    onHeaderMenuToggleItem?              : ((event: { source: Grid, menu: Menu, item: MenuItem, column: Column, checked: boolean }) => void)|string
    onHide?                              : ((event: { source: Widget }) => void)|string
    onHistogramDataCacheSet?             : ((event: { source: TimelineHistogram, record: Model, data: object }) => void)|string
    onMouseOut?                          : ((event: { event: MouseEvent }) => void)|string
    onMouseOver?                         : ((event: { event: MouseEvent }) => void)|string
    onPaint?                             : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onPaste?                             : ((event: { source: Grid, clipboardData: string, modifiedRecords: Model[], targetCell: Location, entityName: string }) => void)|string
    onPdfExport?                         : ((event: { response?: any, error?: Error }) => void)|string
    onPointerOver?                       : ((event: { sourceThe: Tooltip, event: Event }) => void)|string
    onPresetChange?                      : ((event: { source: Scheduler, startDate: Date, centerDate: Date, endDate: Date, from: ViewPreset, to: ViewPreset }) => boolean|void)|string
    onReadOnly?                          : ((event: { readOnly: boolean }) => void)|string
    onRecompose?                         : (() => void)|string
    onRenderRow?                         : ((event: { source: Grid, row: Row, record: Model, recordIndex: number }) => void)|string
    onRenderRows?                        : ((event: { source: Grid }) => void)|string
    onResize?                            : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onResourceMenuBeforeShow?            : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column, resourceRecord: SchedulerResourceModel }) => boolean|void)|string
    onResourceMenuItem?                  : ((event: { source: Scheduler, menu: Menu, item: MenuItem, record: SchedulerResourceModel }) => void)|string
    onResourceMenuShow?                  : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, record: SchedulerResourceModel }) => void)|string
    onResourceMenuToggleItem?            : ((event: { source: Scheduler, menu: Menu, item: MenuItem, resourceRecord: SchedulerResourceModel, checked: boolean }) => void)|string
    onResourceNonWorkingTimeClick?       : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeContextMenu? : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeDblClick?    : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeMouseDown?   : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeMouseOut?    : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeMouseOver?   : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceNonWorkingTimeMouseUp?     : ((event: { source: SchedulerPro, feature: ResourceNonWorkingTime, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeClick?            : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeContextMenu?      : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeDblClick?         : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseDown?        : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseOut?         : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseOver?        : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResourceTimeRangeMouseUp?          : ((event: { source: Scheduler, feature: ResourceTimeRanges, resourceTimeRangeRecord: ResourceTimeRangeModel, resourceRecord: SchedulerResourceModel, domEvent: MouseEvent }) => void)|string
    onResponsive?                        : ((event: { grid: Grid, level: string, width: number, oldLevel: string, oldWidth: number }) => void)|string
    onScroll?                            : ((event: { source: Grid, scrollTop: number }) => void)|string
    onSelectionChange?                   : ((event: { action: 'select'|'deselect', mode: 'row'|'cell', source: Grid, deselected: Model[], selected: Model[], selection: Model[], deselectedCells: Location[], selectedCells: Location[], cellSelection: Location[] }) => void)|string
    onSelectionModeChange?               : ((event: { selectionMode: object }) => void)|string
    onShow?                              : ((event: { source: Widget }) => void)|string
    onSplit?                             : ((event: { subViews: GridBase[], options: { direction: 'horizontal'|'vertical'|'both', atColumn: Column, atRecord: Model } }) => void)|string
    onSplitterCollapseClick?             : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => boolean|void)|string
    onSplitterDragEnd?                   : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onSplitterDragStart?                 : ((event: { source: Grid, subGrid: SubGrid, domEvent: Event }) => void)|string
    onStartCellEdit?                     : ((event: { source: Grid, editorContext: { editor: Editor, column: Column, record: Model, cell: HTMLElement, value: any } }) => void)|string
    onSubGridCollapse?                   : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onSubGridExpand?                     : ((event: { source: Grid, subGrid: SubGrid }) => void)|string
    onTimeAxisChange?                    : ((event: { source: Scheduler, config: { startDate: Date, endDate: Date } }) => void)|string
    onTimeAxisHeaderClick?               : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderContextMenu?         : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderDblClick?            : ((event: { source: TimeAxisColumn|VerticalTimeAxisColumn, startDate: Date, endDate: Date, event: Event }) => void)|string
    onTimeAxisHeaderMenuBeforeShow?      : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => boolean|void)|string
    onTimeAxisHeaderMenuItem?            : ((event: { source: Scheduler, menu: Menu, item: MenuItem, column: Column }) => void)|string
    onTimeAxisHeaderMenuShow?            : ((event: { source: Scheduler, menu: Menu, items: Record<string, Partial<MenuItemConfig>>, column: Column }) => void)|string
    onTimelineContextChange?             : ((event: { oldContext: TimelineContext, context: TimelineContext }) => void)|string
    onTimelineViewportResize?            : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onTimeRangeHeaderClick?              : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onTimeRangeHeaderContextMenu?        : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onTimeRangeHeaderDblClick?           : ((event: { source: Scheduler, timeRangeRecord: TimeSpan, event: MouseEvent, domEvent: MouseEvent }) => void)|string
    onToggleGroup?                       : ((event: { groupRecord: Model, collapse: boolean }) => void)|string
    onToggleNode?                        : ((event: { record: Model, collapse: boolean }) => void)|string
    onToolClick?                         : ((event: { source: Tool, tool: Tool }) => void)|string
    onUnsplit?                           : (() => void)|string
    onVisibleDateRangeChange?            : ((event: { source: Scheduler, old: { startDate: Date, endDate: Date }, new: { startDate: Date, endDate: Date } }) => void)|string

     // Features
    cellCopyPasteFeature?          : object|boolean|string|CellCopyPaste|Partial<CellCopyPasteConfig>
    cellEditFeature?               : object|boolean|string|CellEdit|Partial<CellEditConfig>
    cellMenuFeature?               : object|boolean|string|CellMenu|Partial<CellMenuConfig>
    cellTooltipFeature?            : object|boolean|string|CellTooltip|Partial<CellTooltipConfig>
    columnAutoWidthFeature?        : object|boolean|string|ColumnAutoWidth|Partial<ColumnAutoWidthConfig>
    columnDragToolbarFeature?      : object|boolean|string|ColumnDragToolbar|Partial<ColumnDragToolbarConfig>
    columnLinesFeature?            : object|boolean|string|ColumnLines|Partial<ColumnLinesConfig>
    columnPickerFeature?           : object|boolean|string|ColumnPicker|Partial<ColumnPickerConfig>
    columnRenameFeature?           : object|boolean|string|ColumnRename|Partial<ColumnRenameConfig>
    columnReorderFeature?          : object|boolean|string|ColumnReorder|Partial<ColumnReorderConfig>
    columnResizeFeature?           : object|boolean|string|ColumnResize|Partial<ColumnResizeConfig>
    excelExporterFeature?          : object|boolean|string|GridExcelExporter|Partial<GridExcelExporterConfig>
    fileDropFeature?               : object|boolean|string|FileDrop|Partial<FileDropConfig>
    fillHandleFeature?             : object|boolean|string|FillHandle|Partial<FillHandleConfig>
    filterFeature?                 : object|boolean|string|Filter|Partial<FilterConfig>
    filterBarFeature?              : object|boolean|string|FilterBar|Partial<FilterBarConfig>
    groupFeature?                  : object|boolean|string|Group|Partial<GroupConfig>
    groupSummaryFeature?           : object|boolean|string|GroupSummary|Partial<GroupSummaryConfig>
    headerMenuFeature?             : object|boolean|string|HeaderMenu|Partial<HeaderMenuConfig>
    headerZoomFeature?             : object|boolean|string|HeaderZoom|Partial<HeaderZoomConfig>
    mergeCellsFeature?             : object|boolean|string|MergeCells|Partial<MergeCellsConfig>
    nonWorkingTimeFeature?         : object|boolean|string|NonWorkingTime|Partial<NonWorkingTimeConfig>
    panFeature?                    : object|boolean|string|Pan|Partial<PanConfig>
    pdfExportFeature?              : object|boolean|string|GridPdfExport|Partial<GridPdfExportConfig>
    printFeature?                  : object|boolean|string|GridPrint|Partial<GridPrintConfig>
    quickFindFeature?              : object|boolean|string|QuickFind|Partial<QuickFindConfig>
    regionResizeFeature?           : object|boolean|string|RegionResize|Partial<RegionResizeConfig>
    resourceMenuFeature?           : object|boolean|string|ResourceMenu|Partial<ResourceMenuConfig>
    resourceNonWorkingTimeFeature? : object|boolean|string|ResourceNonWorkingTime|Partial<ResourceNonWorkingTimeConfig>
    resourceTimeRangesFeature?     : object|boolean|string|ResourceTimeRanges|Partial<ResourceTimeRangesConfig>
    rowCopyPasteFeature?           : object|boolean|string|RowCopyPaste|Partial<RowCopyPasteConfig>
    rowExpanderFeature?            : object|boolean|string|RowExpander|Partial<RowExpanderConfig>
    rowReorderFeature?             : object|boolean|string|RowReorder|Partial<RowReorderConfig>
    rowResizeFeature?              : object|boolean|string|GridRowResize|Partial<GridRowResizeConfig>
    scheduleTooltipFeature?        : object|boolean|string|ScheduleTooltip|Partial<ScheduleTooltipConfig>
    searchFeature?                 : object|boolean|string|Search|Partial<SearchConfig>
    sortFeature?                   : object|boolean|string|Sort|Partial<SortConfig>
    splitFeature?                  : object|boolean|string|Split|Partial<SplitConfig>
    stickyCellsFeature?            : object|boolean|string|StickyCells|Partial<StickyCellsConfig>
    stripeFeature?                 : object|boolean|string|Stripe|Partial<StripeConfig>
    summaryFeature?                : object|boolean|string|Summary|Partial<SummaryConfig>
    timeAxisHeaderMenuFeature?     : object|boolean|string|TimeAxisHeaderMenu|Partial<TimeAxisHeaderMenuConfig>
    timeRangesFeature?             : object|boolean|string|TimeRanges|Partial<TimeRangesConfig>
    timeSpanHighlightFeature?      : object|boolean|string|TimeSpanHighlight|Partial<TimeSpanHighlightConfig>
    treeFeature?                   : object|boolean|string|Tree|Partial<TreeConfig>
    treeGroupFeature?              : object|boolean|string|TreeGroup|Partial<TreeGroupConfig>

}

export class BryntumResourceHistogram extends React.Component<BryntumResourceHistogramProps> {

    static instanceClass = ResourceHistogram;

    static instanceName = 'ResourceHistogram';

    processWidgetContent = processWidgetContent;

    // Component is View class
    static isView = true;

    // Hidden div to host unused portals DOM
    portalsCache? : HTMLElement;

    // CSS class used for individual portal containers
    portalContainerClass = 'b-react-portal-container';

    state = {
        // Holds React portals
        portals : new Map(),

        // Needed to trigger refresh when portals change
        generation : 0
    };

    static featureNames = [
        'cellCopyPasteFeature',
        'cellEditFeature',
        'cellMenuFeature',
        'cellTooltipFeature',
        'columnAutoWidthFeature',
        'columnDragToolbarFeature',
        'columnLinesFeature',
        'columnPickerFeature',
        'columnRenameFeature',
        'columnReorderFeature',
        'columnResizeFeature',
        'excelExporterFeature',
        'fileDropFeature',
        'fillHandleFeature',
        'filterFeature',
        'filterBarFeature',
        'groupFeature',
        'groupSummaryFeature',
        'headerMenuFeature',
        'headerZoomFeature',
        'mergeCellsFeature',
        'nonWorkingTimeFeature',
        'panFeature',
        'pdfExportFeature',
        'printFeature',
        'quickFindFeature',
        'regionResizeFeature',
        'resourceMenuFeature',
        'resourceNonWorkingTimeFeature',
        'resourceTimeRangesFeature',
        'rowCopyPasteFeature',
        'rowExpanderFeature',
        'rowReorderFeature',
        'rowResizeFeature',
        'scheduleTooltipFeature',
        'searchFeature',
        'sortFeature',
        'splitFeature',
        'stickyCellsFeature',
        'stripeFeature',
        'summaryFeature',
        'timeAxisHeaderMenuFeature',
        'timeRangesFeature',
        'timeSpanHighlightFeature',
        'treeFeature',
        'treeGroupFeature'
    ];

    projectStores = false;

    dataStores = {
        'resourceTimeRangeStore': 'resourceTimeRanges',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adopt',
        'aggregateDataEntry',
        'aggregateHistogramDataForGroups',
        'align',
        'allowOver',
        'anchor',
        'anchorToTarget',
        'animateRemovingRows',
        'ariaDescription',
        'ariaLabel',
        'autoAdjustTimeAxis',
        'autoClose',
        'autoHeight',
        'autoHide',
        'autoShow',
        'autoUpdateRecord',
        'barTextEffortUnit',
        'barTipEffortUnit',
        'barTooltipTemplate',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'bufferCoef',
        'bufferThreshold',
        'callOnFunctions',
        'centered',
        'closable',
        'closeAction',
        'collapsed',
        'collapsible',
        'columns',
        'config',
        'constrainTo',
        'contentElementCls',
        'contextMenuTriggerEvent',
        'crudManagerClass',
        'dataModelField',
        'defaultBindProperty',
        'defaultFocus',
        'defaultRegion',
        'defaultResourceImageName',
        'defaults',
        'destroyStore',
        'destroyStores',
        'disableGridRowModelWarning',
        'dismissDelay',
        'dock',
        'draggable',
        'durationDisplayPrecision',
        'effortFormat',
        'effortUnit',
        'enableRecurringEvents',
        'enableSticky',
        'enableTextSelection',
        'endParamName',
        'features',
        'fillLastColumn',
        'fixedRowHeight',
        'floating',
        'focusOnToFront',
        'footer',
        'forElement',
        'forSelector',
        'fullRowRefresh',
        'getBarText',
        'getBarTip',
        'getDataEntryForAggregating',
        'getHtml',
        'getOutlineClass',
        'getRecordData',
        'getRectClass',
        'getRowHeight',
        'hardRefreshOnTimeAxisReconfigure',
        'header',
        'hideAnimation',
        'hideDelay',
        'hideHorizontalScrollbar',
        'hideOnDelegateChange',
        'hideWhenEmpty',
        'histogramWidgetClass',
        'hoverDelay',
        'htmlCls',
        'ignoreDomEventsWhileScrolling',
        'ignoreParentReadOnly',
        'includeInactiveEvents',
        'infiniteScroll',
        'initAggregatedDataEntry',
        'itemCls',
        'keyMap',
        'lazyItems',
        'listeners',
        'loadingMsg',
        'loadMask',
        'loadMaskDefaults',
        'loadMaskError',
        'localeClass',
        'localizable',
        'localizableProperties',
        'managedEventSizing',
        'maskDefaults',
        'masked',
        'maximizable',
        'maximized',
        'maxTimeAxisUnit',
        'modal',
        'monitorResize',
        'mouseOffsetX',
        'mouseOffsetY',
        'namedItems',
        'owner',
        'partner',
        'passStartEndParameters',
        'plugins',
        'positioned',
        'preserveFocusOnDatasetChange',
        'preserveScrollOnDatasetChange',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'resizeToFitIncludesHeader',
        'resourceColumns',
        'resourceImageExtension',
        'resourceImagePath',
        'responsiveLevels',
        'ripple',
        'rootElement',
        'scaleColumn',
        'scrollAction',
        'scrollerClass',
        'scrollManager',
        'selectionMode',
        'series',
        'showAnimation',
        'showBarText',
        'showDirty',
        'showEffortUnit',
        'showMaxEffort',
        'showOnClick',
        'showOnHover',
        'showTooltipWhenDisabled',
        'snapRelativeToEventStartDate',
        'startParamName',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'stickyHeaders',
        'strips',
        'subGridConfigs',
        'suppressFit',
        'syncMask',
        'tab',
        'tag',
        'tbar',
        'textAlign',
        'textContent',
        'timeAxis',
        'trackMouse',
        'trapFocus',
        'ui',
        'visibleZoomFactor',
        'weekStartDay',
        'weight',
        'zoomKeepsOriginalTimespan',
        'zoomOnMouseWheel',
        'zoomOnTimeAxisDoubleClick'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'animateTreeNodeToggle',
        'appendTo',
        'barMargin',
        'cls',
        'columnLines',
        'content',
        'crudManager',
        'data',
        'dataset',
        'disabled',
        'displayDateFormat',
        'emptyText',
        'enableEventAnimations',
        'enableUndoRedoKeys',
        'endDate',
        'eventColor',
        'eventStyle',
        'extraData',
        'fillTicks',
        'flex',
        'forceFit',
        'height',
        'hidden',
        'hideFooters',
        'hideHeaders',
        'hideRowHover',
        'histogramWidget',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'longPressTime',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'maxZoomLevel',
        'minHeight',
        'minWidth',
        'minZoomLevel',
        'onBeforeAssignmentDelete',
        'onBeforeCancelCellEdit',
        'onBeforeCellDelete',
        'onBeforeCellEditStart',
        'onBeforeClose',
        'onBeforeColumnDragStart',
        'onBeforeColumnDropFinalize',
        'onBeforeCopy',
        'onBeforeDestroy',
        'onBeforeEventDelete',
        'onBeforeFinishCellEdit',
        'onBeforeHide',
        'onBeforeHistogramDataCacheSet',
        'onBeforePan',
        'onBeforePaste',
        'onBeforePdfExport',
        'onBeforePresetChange',
        'onBeforeRenderHistogramRow',
        'onBeforeRenderRecordHistogram',
        'onBeforeRenderRow',
        'onBeforeRenderRows',
        'onBeforeSelectionChange',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onBeforeToggleGroup',
        'onBeforeToggleNode',
        'onCancelCellEdit',
        'onCatchAll',
        'onCellClick',
        'onCellContextMenu',
        'onCellDblClick',
        'onCellMenuBeforeShow',
        'onCellMenuItem',
        'onCellMenuShow',
        'onCellMenuToggleItem',
        'onCellMouseOut',
        'onCellMouseOver',
        'onCollapse',
        'onCollapseNode',
        'onColumnDrag',
        'onColumnDragStart',
        'onColumnDrop',
        'onContextMenuItem',
        'onContextMenuToggleItem',
        'onCopy',
        'onDataChange',
        'onDateRangeChange',
        'onDestroy',
        'onDragSelecting',
        'onExpand',
        'onExpandNode',
        'onFileDrop',
        'onFinishCellEdit',
        'onFocusIn',
        'onFocusOut',
        'onGenerateScalePoints',
        'onGridRowAbort',
        'onGridRowBeforeDragStart',
        'onGridRowBeforeDropFinalize',
        'onGridRowDrag',
        'onGridRowDragStart',
        'onGridRowDrop',
        'onHeaderMenuBeforeShow',
        'onHeaderMenuItem',
        'onHeaderMenuShow',
        'onHeaderMenuToggleItem',
        'onHide',
        'onHistogramDataCacheSet',
        'onMouseOut',
        'onMouseOver',
        'onPaint',
        'onPaste',
        'onPdfExport',
        'onPointerOver',
        'onPresetChange',
        'onReadOnly',
        'onRecompose',
        'onRenderRow',
        'onRenderRows',
        'onResize',
        'onResourceMenuBeforeShow',
        'onResourceMenuItem',
        'onResourceMenuShow',
        'onResourceMenuToggleItem',
        'onResourceNonWorkingTimeClick',
        'onResourceNonWorkingTimeContextMenu',
        'onResourceNonWorkingTimeDblClick',
        'onResourceNonWorkingTimeMouseDown',
        'onResourceNonWorkingTimeMouseOut',
        'onResourceNonWorkingTimeMouseOver',
        'onResourceNonWorkingTimeMouseUp',
        'onResourceTimeRangeClick',
        'onResourceTimeRangeContextMenu',
        'onResourceTimeRangeDblClick',
        'onResourceTimeRangeMouseDown',
        'onResourceTimeRangeMouseOut',
        'onResourceTimeRangeMouseOver',
        'onResourceTimeRangeMouseUp',
        'onResponsive',
        'onScroll',
        'onSelectionChange',
        'onSelectionModeChange',
        'onShow',
        'onSplit',
        'onSplitterCollapseClick',
        'onSplitterDragEnd',
        'onSplitterDragStart',
        'onStartCellEdit',
        'onSubGridCollapse',
        'onSubGridExpand',
        'onTimeAxisChange',
        'onTimeAxisHeaderClick',
        'onTimeAxisHeaderContextMenu',
        'onTimeAxisHeaderDblClick',
        'onTimeAxisHeaderMenuBeforeShow',
        'onTimeAxisHeaderMenuItem',
        'onTimeAxisHeaderMenuShow',
        'onTimelineContextChange',
        'onTimelineViewportResize',
        'onTimeRangeHeaderClick',
        'onTimeRangeHeaderContextMenu',
        'onTimeRangeHeaderDblClick',
        'onToggleGroup',
        'onToggleNode',
        'onToolClick',
        'onUnsplit',
        'onVisibleDateRangeChange',
        'preserveScroll',
        'presets',
        'project',
        'readOnly',
        'record',
        'resourceMargin',
        'resourceTimeRanges',
        'resourceTimeRangeStore',
        'rowHeight',
        'rowLines',
        'scrollable',
        'showBarTip',
        'snap',
        'startDate',
        'store',
        'strictRecordMapping',
        'tickSize',
        'timeRanges',
        'timeRangeStore',
        'timeZone',
        'title',
        'tools',
        'tooltip',
        'transitionDuration',
        'viewPreset',
        'visibleDate',
        'width',
        'workingTime',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'eventColors',
        'eventStyles',
        'isEngineReady',
        'isSettingValues',
        'isValid',
        'scrollLeft',
        'scrollTop',
        'scrollX',
        'selectedCell',
        'selectedCells',
        'selectedRecord',
        'selectedRecords',
        'selectedRows',
        'state',
        'timeResolution',
        'type',
        'values',
        'zoomLevel'
    ];

    // Component instance
    instance!: ResourceHistogram;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumResourceHistogramProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-resource-histogram-container`;
        return (
            <React.Fragment>
                <div className={className} ref={(element) => (this.element = element!)}>
                    {Array.from(this.state.portals).map((item) => item[1])}
                </div>
                <div ref={el => (this.portalsCache = el!)} className="b-react-portals-cache" style={{ display : 'none' }}></div>
            </React.Fragment>
        );

    }
}
